<template>
  <div class="manage-forms-container">
    <ContentContainerNavbar :title="$t('i18n.manageFormsTitle', { records: filteredForms().length })"
      :button="!$vuetify.display.smAndDown" :buttonText="$t('i18n.createForm')" buttonEvent="create-form-btn"
      @create-form-btn="goToCreateFormContainer" />
    <v-skeleton-loader v-if="loading" type="table"></v-skeleton-loader>
    <v-table v-else class="table" fixed-header :hover="formDeviceAssociationList.length !== 0">
      <thead>
        <tr>
          <th class="text-left font-weight-black checkbox-cell">
            <v-icon class="cursor-pointer pr-6" :icon="filterActive ? 'mdi-filter-remove' : 'mdi-filter-menu'"
              @click="toggleFilter"></v-icon>
            {{ $t('i18n.name') }}
          </th>
          <th class="text-left font-weight-black">
            {{ $t('i18n.description') }}
          </th>
          <th class="text-left font-weight-black">
            {{ $t('i18n.associatedDevices') }}
          </th>
          <th v-if="!$vuetify.display.smAndDown" class="text-left font-weight-black">
          </th>
        </tr>
        <tr v-if="filterActive">
          <th class="text-left font-weight-black">
            <v-text-field v-model="filters.name" density="compact" variant="outlined" :hide-details="true"
              clearable></v-text-field>
          </th>
          <th class="text-left font-weight-black">
            <v-text-field v-model="filters.description" density="compact" variant="outlined" :hide-details="true"
              clearable></v-text-field>
          </th>
          <th class="text-left font-weight-black">
          </th>
          <th v-if="!$vuetify.display.smAndDown" class="text-left font-weight-black">
          </th>
        </tr>
      </thead>
      <tbody v-if="formDeviceAssociationList.length !== 0">
        <tr class="text-left" v-for="(form, index) in filteredForms()" :key="form.id">
          <td>{{ form.name }}</td>
          <td>{{ form.description }}</td>
          <td class="text-left">
            <v-chip variant="flat" v-for="device in form.associated_devices" :key="device.id" class="ma-2 gorate-bg cursor-pointer"
              label @click="goToDevice()">{{ device.name }}</v-chip>
          </td>
          <td v-if="!$vuetify.display.smAndDown" class="action-buttons-container text-right">
            <div class="edit-device-btn-container">
              <v-btn class="mr-3 cursor-pointer" variant="text" icon @click="editForm(form.id)">
                <v-icon class="cursor-pointer" size="large" icon="$GoRateEdit"></v-icon>
              </v-btn>
            </div>
            <div class="delete-device-btn-container">
              <v-btn class="cursor-pointer" variant="text" icon @click="openDeleteFormModal(index)"
                :disabled="formDeletionDisabled(index)">
                <v-icon size="small" icon="$GoRateDeleteOutline"></v-icon>
              </v-btn>
              <v-tooltip v-if="formDeletionDisabled(index)" activator="parent" location="start">
                {{ getDeleteBtnTooltipText(index) }}
              </v-tooltip>
            </div>
          </td>
        </tr>
      </tbody>
      <tbody v-else>
        <tr class="no-hover">
          <td colspan="4" class="text-center">{{ $t('i18n.noFormsAvailable') }}</td>
        </tr>
      </tbody>
    </v-table>
  </div>
  <!--
   Dialogs are below
  -->
  <v-dialog v-model="deleteFormModalIsActive" max-width="400">
    <DeleteDeviceModal :formID="selectedFormID" @close-modal="closeDeleteFormModal" />
  </v-dialog>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { useRouter } from 'vue-router'

import { FormDeviceAssociation } from '@/utils/DataTypes'
import ContentContainerNavbar from '../navbars/ContentContainerNavbar.vue'
import DeleteDeviceModal from '../modals/form_modals/DeleteFormModal.vue'
import { useAppStore } from '@/stores/appStore'
import { api } from '@/utils/HelperFunctions'

export default defineComponent({
  name: 'ManageForms',
  setup () {
    const router = useRouter()
    const appStore = useAppStore()
    return {
      router,
      appStore
    }
  },
  async mounted () {
    const [a, b] = await Promise.all([
      this.getFormDeletionStatus(),
      this.getAllForms()])

    this.formDeletionStatusList = a as any
    this.formDeviceAssociationList = b as any
    this.loading = false
  },
  components: {
    ContentContainerNavbar,
    DeleteDeviceModal
  },
  data () {
    return {
      formDeviceAssociationList: [] as FormDeviceAssociation[],
      formDeletionStatusList: [] as any[],
      filters: {
        name: '',
        description: '',
        associated_devices: ''
      },

      selectedFormID: -1,

      deleteFormModalIsActive: false,
      formAlertIsActive: false,
      filterActive: false,
      loading: true,

      searchQuery: '',
      alertMessage: '',
      alertStyle: ''
    }
  },
  methods: {
    async getFormDeletionStatus () {
      try {
        const url = '/forms-deletion-status'
        const getFormDeletionStatusResponse = await api(url, 'get')

        if (!getFormDeletionStatusResponse) return

        return getFormDeletionStatusResponse.data
      } catch (error) {
        this.appStore.addAlert('error', 'Error getting form deletion status records.')
      }
    },
    async getAllForms () {
      try {
        const url = '/forms-devices-association'
        const getFormDeviceAssociationResponse = await api(url, 'get')

        if (!getFormDeviceAssociationResponse) return

        return getFormDeviceAssociationResponse.data
      } catch (error) {
        this.appStore.addAlert('error', 'Error getting simplified form list: ')
      }
    },
    async closeDeleteFormModal () {
      this.formDeviceAssociationList = await this.getAllForms() as any
      this.deleteFormModalIsActive = false
    },
    navigate (routeName: string) {
      this.router.push({ name: routeName })
    },
    goToCreateFormContainer () {
      this.formAlertIsActive = false
      this.router.push({ name: 'CreateForm' })
    },
    editForm (formID: number) {
      this.formAlertIsActive = false
      this.router.push({ name: 'EditForm', params: { formIdProp: formID } })
    },
    openDeleteFormModal (index: number) {
      this.formAlertIsActive = false
      const filteredForms = this.filteredForms()
      const formDeviceAssociationRecord = filteredForms[index]
      this.selectedFormID = formDeviceAssociationRecord?.id ?? -1
      this.deleteFormModalIsActive = true
    },
    formDeletionDisabled (index: number): boolean {
      const formDeviceAssociationRecord: FormDeviceAssociation = this.filteredForms()[index]
      if (!formDeviceAssociationRecord) return true

      const formDeletionStatus = this.formDeletionStatusList.find(s => s.form_id === formDeviceAssociationRecord.id)

      if (!formDeletionStatus) return false

      return formDeletionStatus.has_responses === 1 || formDeletionStatus.has_device === 1
    },
    getDeleteBtnTooltipText (index: number): string {
      const formDeviceAssociationRecord: FormDeviceAssociation = this.filteredForms()[index]
      if (!formDeviceAssociationRecord) return ''

      const formDeletionStatus = this.formDeletionStatusList.find(s => s.form_id === formDeviceAssociationRecord.id)

      if (!formDeletionStatus) return ''

      if (formDeletionStatus.has_responses === 1) {
        return this.$t('i18n.deleteDeviceTooltip1')
      }

      if (formDeletionStatus.has_device === 1) {
        return this.$t('i18n.deleteDeviceTooltip2')
      }

      return ''
    },
    handleFormSearchQuery (newSearchQuery: string) {
      this.searchQuery = newSearchQuery
    },
    filteredForms () {
      let filteredFormDeviceAssociations = this.formDeviceAssociationList as FormDeviceAssociation[]

      if (this.filters.name) {
        filteredFormDeviceAssociations = filteredFormDeviceAssociations.filter((formDeviceAssociationObject: FormDeviceAssociation) =>
          formDeviceAssociationObject.name.toLowerCase().includes(this.filters.name.toLowerCase())
        )
      }
      if (this.filters.description) {
        filteredFormDeviceAssociations = filteredFormDeviceAssociations.filter((formDeviceAssociationObject: FormDeviceAssociation) =>
          formDeviceAssociationObject.description?.toLowerCase().includes(this.filters.description.toLowerCase())
        )
      }
      console.log('filteredFormDeviceAssociations:', filteredFormDeviceAssociations)
      return filteredFormDeviceAssociations.sort((a: FormDeviceAssociation, b: FormDeviceAssociation) => {
        if (a.id == null && b.id == null) return 0
        if (a.id == null) return 1
        if (b.id == null) return -1
        return b.id - a.id // DESC
      })
      // return filteredFormDeviceAssociations
    },
    toggleFilter () {
      this.filterActive = !this.filterActive
      this.filters = {
        name: '',
        description: '',
        associated_devices: ''
      }
    },
    goToDevice () {
      this.router.push({ name: 'Devices' })
    }
  }
})
</script>

<style lang="scss" scoped>
@import "../../styles/colors.scss";

.manage-forms-container {
  display: grid;
  grid-template-rows: auto 1fr;
  height: 100%;

  box-shadow: 2px 0 15px 0 rgba(0, 0, 0, 0.08);

  .table {
    margin: 1.0em;
    padding: 1.0em 2.0em;
    border-radius: 15px;
    overflow-y: auto;

    .checkbox-cell {
      display: grid;
      grid-template-columns: auto auto;
      justify-content: left;
      align-items: center;
    }

    .action-buttons-container {
      display: grid;
      grid-template-columns: 1fr auto;
    }
  }
}
</style>
